import {
  type TerraUIMessage,
  type UserConfirmationMessage,
  type MentionEntity,
  type MessageSegment,
  DataMessageTypeEnum,
  ExternalToolResultsResumeSchema,
  isTerminalToolName,
  ASK_USER_TOOL_NAME,
} from '@terra/shared-dto-chat-types';
import { hasValue, isToolCallData } from './tool-call-data-merge';
import {
  getMentionEntitiesFromUserMessage,
  getMentionSegmentsFromUserMessage,
} from './userMessageDisplayText';
import {
  getMetadataAsUserConfirmation,
  isDataOrConfirmationPart,
  isUserConfirmationData,
  parseBaseDataMessage,
} from './hitl-confirmation-matching';
import type { ProcessedMessage } from './message-processing-types';

type AskUserAnswerRecord = {
  answer: string;
  mentions: MentionEntity[];
  segments: MessageSegment[] | null;
};

export function collectAskUserAnswers(
  messages: TerraUIMessage[],
): Map<string, AskUserAnswerRecord> {
  const answersByToolCallId = new Map<string, AskUserAnswerRecord>();

  const registerConfirmation = ({
    message,
    confirmation,
  }: {
    message: TerraUIMessage;
    confirmation: UserConfirmationMessage;
  }) => {
    if (confirmation.toolName !== ASK_USER_TOOL_NAME) return;
    const answer = confirmation.response;

    if (typeof answer !== 'string') return;

    const mentions = getMentionEntitiesFromUserMessage({ message });
    const segments = getMentionSegmentsFromUserMessage({ message });

    for (const targetId of confirmation.subs ?? []) {
      if (!answersByToolCallId.has(targetId)) {
        answersByToolCallId.set(targetId, {
          answer,
          mentions,
          segments,
        });
      }
    }
  };

  for (const message of messages) {
    const metaConfirmation = getMetadataAsUserConfirmation(message.metadata);

    if (metaConfirmation) {
      registerConfirmation({ message, confirmation: metaConfirmation });
    }

    for (const part of message.parts ?? []) {
      if (!isDataOrConfirmationPart(part) || !part.data) continue;
      const partData = parseBaseDataMessage(part.data);

      if (partData && isUserConfirmationData(partData)) {
        registerConfirmation({ message, confirmation: partData });
      }
    }
  }

  return answersByToolCallId;
}

export function enrichAskUserToolCalls({
  processedMessages,
  answersByToolCallId,
}: {
  processedMessages: ProcessedMessage[];
  answersByToolCallId: Map<string, AskUserAnswerRecord>;
}): void {
  if (answersByToolCallId.size === 0) return;

  for (const processedMessage of processedMessages) {
    processedMessage.parts.forEach((processedPart, partIndex) => {
      const dataMessage = processedPart.dataMessage;

      if (
        processedPart.kind !== 'data-message' ||
        !dataMessage ||
        !isToolCallData(dataMessage) ||
        dataMessage.toolName !== ASK_USER_TOOL_NAME
      ) {
        return;
      }

      const answerRecord = answersByToolCallId.get(dataMessage.toolCallId);

      if (answerRecord === undefined) return;

      const existingOutput =
        dataMessage.output && typeof dataMessage.output === 'object'
          ? (dataMessage.output as Record<string, unknown>)
          : {};

      const hasAnswerMentions =
        answerRecord.segments !== null &&
        answerRecord.segments.length > 0 &&
        answerRecord.mentions.length > 0;

      processedMessage.parts[partIndex] = {
        ...processedPart,
        dataMessage: {
          ...dataMessage,
          output: {
            ...existingOutput,
            selectedLabel: answerRecord.answer,
            ...(hasAnswerMentions
              ? {
                  answerSegments: answerRecord.segments,
                  answerMentions: answerRecord.mentions,
                }
              : {}),
          },
        },
      };
    });
  }
}

type ExternalToolResultEntry = {
  result: unknown;
  status: 'success' | 'error';
};

export function collectExternalToolResults(
  messages: TerraUIMessage[],
): Map<string, ExternalToolResultEntry> {
  const resultsByToolCallId = new Map<string, ExternalToolResultEntry>();

  for (const message of messages) {
    const parsed = ExternalToolResultsResumeSchema.safeParse(message.metadata);

    if (!parsed.success) {
      continue;
    }

    for (const item of parsed.data.results) {
      if (isTerminalToolName(item.toolName)) {
        continue;
      }

      resultsByToolCallId.set(item.toolCallId, {
        result: item.result,
        status: item.status,
      });
    }
  }

  return resultsByToolCallId;
}

export function enrichExternalToolCalls({
  processedMessages,
  resultsByToolCallId,
}: {
  processedMessages: ProcessedMessage[];
  resultsByToolCallId: Map<string, ExternalToolResultEntry>;
}): void {
  if (resultsByToolCallId.size === 0) return;

  for (const processedMessage of processedMessages) {
    processedMessage.parts.forEach((processedPart, partIndex) => {
      const dataMessage = processedPart.dataMessage;

      if (
        processedPart.kind !== 'data-message' ||
        !dataMessage ||
        !isToolCallData(dataMessage) ||
        hasValue(dataMessage.output)
      ) {
        return;
      }

      const externalResult = resultsByToolCallId.get(dataMessage.toolCallId);

      if (!externalResult) return;

      const status = externalResult.status === 'error' ? 'error' : 'completed';

      processedMessage.parts[partIndex] = {
        ...processedPart,
        dataMessage: {
          ...dataMessage,
          status,
          type:
            status === 'error'
              ? DataMessageTypeEnum.ToolCallError
              : DataMessageTypeEnum.ToolCallComplete,
          output: externalResult.result,
        },
      };
    });
  }
}
